import { useEffect, useMemo, useState } from 'react';
import type { AudienceItem, AudiencePlatformItem, AudienceResponseMeta, CountryItem } from '@digiseg-labs/api-client';
import { TaxonomyApi } from '@digiseg-labs/api-client';

type UseAudienceParams = {
  audienceCode?: string;
  platform?: AudiencePlatformItem | string;
  country?: CountryItem | string;
  enabled?: boolean;
};

export const useAudience = ({
  audienceCode,
  platform,
  country,
  enabled = true,
}: UseAudienceParams = {}) => {
  const [audience, setAudience] = useState<AudienceItem | undefined>(undefined);
  const [meta, setMeta] = useState<AudienceResponseMeta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const platformCode = useMemo(() => (
    typeof platform === 'string' ? platform : platform?.code
  ), [platform]);

  const countryCode = useMemo(() => (
    typeof country === 'string' ? country : country?.code
  ), [country]);

  useEffect(() => {
    if (!enabled || !audienceCode) {
      setAudience(undefined);
      setMeta(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const api = new TaxonomyApi();

    api.getAudience(audienceCode, platformCode || undefined, countryCode || undefined)
      .then((resp) => {
        if (cancelled) return;
        setAudience(resp.data.data);
        setMeta(resp.data.meta || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        console.error('Failed to load audience', err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [audienceCode, platformCode, countryCode, enabled]);

  return { audience, meta, isLoading, error };
};
